import React, { useState, useEffect } from 'react';
import './ObligacionesFiscalesScreen.css';
import {
    IoFileTrayFullOutline,
    IoDocumentTextOutline,
    IoBriefcaseOutline,
    IoAnalyticsOutline,
    IoArrowBack,
    IoInformationCircleOutline,
    IoCloseOutline,
    IoSearchOutline,
    IoChevronForwardOutline,
    IoCalendarOutline,
    IoPersonOutline,
    IoHomeOutline,
    IoCashOutline,
    IoReceiptOutline
} from 'react-icons/io5';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { calcularIMSSPatronal } from '../utils/MotorNomina';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';

function ObligacionesFiscalesScreen() {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, userData } = useAuth();

    // Contexto SaaS
    const isSaaS = location.pathname.includes('/superuser/hr');
    const basePath = isSaaS ? '/superuser/hr' : '';
    const activeCondominioId = isSaaS ? 'SAAS' : userData?.condominioId;
    const activeRoute = isSaaS ? "/superuser/hr/obligaciones-fiscales" : "/obligaciones-fiscales";

    // Filtros de Periodo (Inicializar con el mes actual en formato YYYY-MM)
    const [mesSeleccionado, setMesSeleccionado] = useState(() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    });
    const [viewMode, setViewMode] = useState('causado'); // 'causado' o 'flujo'
    const [loading, setLoading] = useState(true);

    // Configuración Manual Admin
    const [saldoFavorIVAAnterior, setSaldoFavorIVAAnterior] = useState(0);
    const [primaRiesgo, setPrimaRiesgo] = useState(0.5); // %
    const [tasaISN, setTasaISN] = useState(3.0); // %

    // --- ESTADOS DE RESULTADOS ---

    // 1. ISR Retenciones (Salarios)
    const [isrCalculado, setIsrCalculado] = useState({
        ingresosExentos: 0,
        ingresosGravados: 0,
        totalIngresosPagados: 0,
        impuestoRetenido: 0,
        cantidadAPagar: 0
    });

    // 1-b. ISR Retenciones (Servicios Profesionales / RESICO / Proveedores)
    const [isrProveedoresCalculado, setIsrProveedoresCalculado] = useState({
        baseServicios: 0,
        impuestoRetenido: 0,
        cantidadAPagar: 0
    });

    // 2. IVA Cruzado
    const [ivaCalculado, setIvaCalculado] = useState({
        actosGravados: 0,
        actosExentos: 0,
        ivaTrasladado: 0,
        ivaAcreditableGastos: 0,
        ivaRetenido: 0, // Honorarios o Resico
        saldoAFavorMes: 0,
        impuestoACargo: 0
    });

    // 3. IMSS Patronal + Obrero
    const [imssCalculado, setImssCalculado] = useState({
        diasCotizados: 0,
        retencionObreraTotal: 0,
        retencionInfonavitTotal: 0,
        cuotaFijaEspecie: 0,
        cuotaExcedente: 0,
        cuotaGastosMedicos: 0,
        cuotaEnDinero: 0,
        cuotaRiesgoTrabajo: 0,
        cuotaInvalidezVida: 0,
        cuotaGuarderias: 0,
        seguroRetiro: 0,
        cesantiaVejez: 0,
        aportacionInfonavit: 0,
        totalCargaPatronal: 0,
        granTotalSIPARE: 0
    });

    // 4. ISN (Estatal)
    const [isnCalculado, setIsnCalculado] = useState({
        baseGravable: 0, // Total Nomina pagada
        impuestoAPagar: 0
    });

    // Listas detalladas para desglose dinámico (Drilldown)
    const [detalleItems, setDetalleItems] = useState({
        actosGravados: [],
        actosExentos: [],
        gastosIva: [],
        gastosRetIva: [],
        gastosRetIsr: [],
        nominas: [],
        nominasExentos: [],
        nominasGravados: [],
        nominasIsr: []
    });

    // Estado del Modal Drilldown
    const [drilldownModal, setDrilldownModal] = useState({
        isOpen: false,
        title: '',
        total: 0,
        items: [],
        type: 'cargos' // 'cargos', 'gastos', 'nominas', 'mixto_iva'
    });
    const [drilldownSearch, setDrilldownSearch] = useState('');

    // Acordeones Activos
    const [expandedSeccion, setExpandedSeccion] = useState('SAT_ISR'); // 'SAT_ISR', 'SAT_ISR_PROV', 'SAT_IVA', 'IMSS', 'ISN'

    useEffect(() => {
        if (!currentUser) return;
        calcularTotalesPeriodo();
    }, [mesSeleccionado, viewMode, currentUser, saldoFavorIVAAnterior, primaRiesgo, tasaISN]);


    const calcularTotalesPeriodo = async () => {
        setLoading(true);
        try {
            const [yearStr, monthStr] = mesSeleccionado.split('-');
            const year = parseInt(yearStr, 10);
            const month = parseInt(monthStr, 10);

            // Funciones de ayuda para fechas
            const getStartEnd = (y, m) => {
                const s = new Date(y, m - 1, 1, 0, 0, 0, 0);
                const e = new Date(y, m, 0, 23, 59, 59, 999);
                return { start: Timestamp.fromDate(s), end: Timestamp.fromDate(e) };
            };

            let ranges = {
                isr_imss_isn: getStartEnd(year, month), // Mensual Causado
                iva: getStartEnd(year, month), // Mensual Causado
                infonavit_rcv: getStartEnd(year, month) // Mensual/Bimestral Causado
            };

            let isInfonavitPayableMonth = true;

            if (viewMode === 'flujo') {
                // Flujo busca el mes / bimestres anteriores
                let prevMonth = month - 1;
                let prevYear = year;
                if (prevMonth === 0) {
                    prevMonth = 12;
                    prevYear = year - 1;
                }
                ranges.isr_imss_isn = getStartEnd(prevYear, prevMonth);
                ranges.iva = getStartEnd(prevYear, prevMonth);

                // INFONAVIT y RCV se pagan bimestralmente en meses impares (1, 3, 5, 7, 9, 11)
                if (month % 2 !== 0) {
                    isInfonavitPayableMonth = true;
                    let startBimMonth = month - 2;
                    let startBimYear = year;
                    if (startBimMonth <= 0) {
                        startBimMonth += 12;
                        startBimYear -= 1;
                    }
                    let endBimMonth = month - 1;
                    let endBimYear = year;
                    if (endBimMonth === 0) {
                        endBimMonth = 12;
                        endBimYear -= 1;
                    }
                    const startBimDate = new Date(startBimYear, startBimMonth - 1, 1, 0, 0, 0, 0);
                    const endBimDate = new Date(endBimYear, endBimMonth, 0, 23, 59, 59, 999);
                    ranges.infonavit_rcv = { start: Timestamp.fromDate(startBimDate), end: Timestamp.fromDate(endBimDate) };
                } else {
                    isInfonavitPayableMonth = false;
                }
            }

            // 1. Consultar Nóminas (ISR, IMSS Mensual, ISN)
            const nominasRef = collection(db, 'nominas_periodos');
            const qNominas = query(
                nominasRef,
                where('fechaGeneracion', '>=', ranges.isr_imss_isn.start),
                where('fechaGeneracion', '<=', ranges.isr_imss_isn.end)
            );

            const querySnapshot = await getDocs(qNominas);
            const nominasList = querySnapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(n => n.condominioId === activeCondominioId)
                .filter(n => n.estatus === 'pagado' || n.estatus === 'Pagado' || n.estatus === 'Timbrado');

            // 1-b. Consultar Nóminas Bimestrales
            let nominasBimestralesList = [];
            if (viewMode === 'flujo' && isInfonavitPayableMonth) {
                const qNominasBim = query(
                    nominasRef,
                    where('fechaGeneracion', '>=', ranges.infonavit_rcv.start),
                    where('fechaGeneracion', '<=', ranges.infonavit_rcv.end)
                );
                const snapshotBim = await getDocs(qNominasBim);
                nominasBimestralesList = snapshotBim.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(n => n.condominioId === activeCondominioId)
                    .filter(n => n.estatus === 'pagado' || n.estatus === 'Pagado' || n.estatus === 'Timbrado');
            } else if (viewMode === 'causado') {
                nominasBimestralesList = nominasList;
            }

            // Acumuladores ISR / Salarios
            let totalBrutoNomina = 0;
            let isrMes = 0;
            let exentosISR = 0;
            let gravadosISR = 0;

            // Acumuladores IMSS / Infonavit
            let sumRetencionObrera = 0;
            let sumRetencionInfonavit = 0;
            let sumDiasCotizados = 0;

            let cFija = 0, cExc = 0, cGas = 0, cDin = 0, cRies = 0, cInv = 0, cGua = 0, cRet = 0, cCes = 0, cInf = 0;

            // Iteración para IMPUESTOS MENSUALES
            nominasList.forEach(n => {
                const sbc = n.sbc || 0;
                const diasTrabajados = n.diasTrabajados || 15;
                const percepcionTotal = n.totalPercepciones || 0;
                const retencionISR = n.deducciones?.isr || 0;
                const retencionIMSSObrero = n.deducciones?.imss || 0;

                totalBrutoNomina += percepcionTotal;
                isrMes += retencionISR;

                const proxyExento = n.conceptosExentosTotal || 0;
                const proxyGravado = percepcionTotal - proxyExento;
                exentosISR += proxyExento;
                gravadosISR += proxyGravado;

                sumRetencionObrera += retencionIMSSObrero;
                sumDiasCotizados += diasTrabajados;

                const calcPatro = calcularIMSSPatronal(sbc, diasTrabajados, Number(primaRiesgo));
                cFija += calcPatro.cuotaFijaEspecie;
                cExc += calcPatro.cuotaExcedenteEspecie;
                cGas += calcPatro.cuotaGastosMedicos;
                cDin += calcPatro.cuotaEnDinero;
                cRies += calcPatro.cuotaRiesgoTrabajo;
                cInv += calcPatro.cuotaInvalidezVida;
                cGua += calcPatro.cuotaGuarderias;
            });

            // Iteración para IMPUESTOS BIMESTRALES
            nominasBimestralesList.forEach(n => {
                const sbc = n.sbc || 0;
                const diasTrabajados = n.diasTrabajados || 15;
                const retencionInfonavit = n.deducciones?.infonavit || 0;

                sumRetencionInfonavit += retencionInfonavit;
                const calcPatro = calcularIMSSPatronal(sbc, diasTrabajados, Number(primaRiesgo));

                cRet += calcPatro.cuotaRetiro;
                cCes += calcPatro.cuotaCesantiaVejez;
                cInf += calcPatro.aportacionInfonavit;
            });

            const totalPatronal = cFija + cExc + cGas + cDin + cRies + cInv + cGua + cRet + cCes + cInf;

            setIsrCalculado({
                ingresosExentos: exentosISR,
                ingresosGravados: gravadosISR,
                totalIngresosPagados: exentosISR + gravadosISR,
                impuestoRetenido: isrMes,
                cantidadAPagar: isrMes
            });

            setImssCalculado({
                diasCotizados: sumDiasCotizados,
                retencionObreraTotal: sumRetencionObrera,
                retencionInfonavitTotal: sumRetencionInfonavit,
                cuotaFijaEspecie: cFija,
                cuotaExcedente: cExc,
                cuotaGastosMedicos: cGas,
                cuotaEnDinero: cDin,
                cuotaRiesgoTrabajo: cRies,
                cuotaInvalidezVida: cInv,
                cuotaGuarderias: cGua,
                seguroRetiro: cRet,
                cesantiaVejez: cCes,
                aportacionInfonavit: cInf,
                totalCargaPatronal: totalPatronal,
                granTotalSIPARE: sumRetencionObrera + sumRetencionInfonavit + totalPatronal
            });

            setIsnCalculado({
                baseGravable: totalBrutoNomina,
                impuestoAPagar: totalBrutoNomina * (Number(tasaISN) / 100)
            });

            // 2. Consultar Facturas Pagadas (Gastos - IVA Acreditable y Retenciones) 
            const gastosRef = collection(db, 'gastos');
            const qGastos = query(
                gastosRef,
                where('fecha', '>=', ranges.iva.start),
                where('fecha', '<=', ranges.iva.end)
            );
            const gastosSnap = await getDocs(qGastos);
            const gastos = gastosSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                .filter(g => g.condominioId === activeCondominioId)
                .filter(g => g.estatus === 'pagado' || g.estatus === 'Pagado');

            let ivaAcreditableGastos = 0;
            let ivaRetenidoGastos = 0;
            let isrRetenidoGastos = 0;
            let baseServiciosGastos = 0;

            const listGastosIva = [];
            const listGastosRetIva = [];
            const listGastosRetIsr = [];

            gastos.forEach(g => {
                const ivaVal = Number(g.iva || 0);
                const retIvaVal = Number(g.retencionIva || 0);
                const retIsrVal = Number(g.retencionIsr || g.retencionISR || g.isrRetenido || 0);

                if (ivaVal > 0) {
                    ivaAcreditableGastos += ivaVal;
                    listGastosIva.push(g);
                }
                if (retIvaVal > 0) {
                    ivaRetenidoGastos += retIvaVal;
                    listGastosRetIva.push(g);
                }
                if (retIsrVal > 0) {
                    isrRetenidoGastos += retIsrVal;
                    baseServiciosGastos += Number(g.subtotal || g.monto || 0);
                    listGastosRetIsr.push(g);
                }
            });

            // 3. Consultar Ingresos Facturados (IVA Trasladado - Cobrado)
            const cargosRef = collection(db, 'cargos');
            const qPagos = query(
                cargosRef,
                where('fechaPago', '>=', ranges.iva.start),
                where('fechaPago', '<=', ranges.iva.end)
            );
            const pagosSnap = await getDocs(qPagos);
            const pagos = pagosSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                .filter(p => p.condominioId === activeCondominioId)
                .filter(p => p.estatus === 'pagado' || p.estatus === 'Pagado');

            let actosGravados = 0;
            let actosExentos = 0;
            let ivaTrasladadoMes = 0;

            const listActosGravados = [];
            const listActosExentos = [];

            pagos.forEach(p => {
                const montoBase = Number(p.monto || p.montoPagado || 0);
                const ivaVal = Number(p.iva || 0);
                if (ivaVal > 0) {
                    actosGravados += (montoBase - ivaVal);
                    ivaTrasladadoMes += ivaVal;
                    listActosGravados.push(p);
                } else {
                    actosExentos += montoBase;
                    listActosExentos.push(p);
                }
            });

            // Cálculo Final de IVA
            let cargoDelMes = ivaTrasladadoMes - ivaAcreditableGastos - ivaRetenidoGastos;
            let saldoAFavorPeriodo = 0;
            let impuestoDefinitivo = 0;

            const saldoFavorPrevio = Number(saldoFavorIVAAnterior);

            if (cargoDelMes < 0) {
                saldoAFavorPeriodo = Math.abs(cargoDelMes) + saldoFavorPrevio;
                impuestoDefinitivo = 0;
            } else {
                cargoDelMes = cargoDelMes - saldoFavorPrevio;
                if (cargoDelMes <= 0) {
                    saldoAFavorPeriodo = Math.abs(cargoDelMes);
                    impuestoDefinitivo = 0;
                } else {
                    saldoAFavorPeriodo = 0;
                    impuestoDefinitivo = cargoDelMes;
                }
            }

            setIsrProveedoresCalculado({
                baseServicios: baseServiciosGastos,
                impuestoRetenido: isrRetenidoGastos,
                cantidadAPagar: isrRetenidoGastos
            });

            setDetalleItems({
                actosGravados: listActosGravados,
                actosExentos: listActosExentos,
                gastosIva: listGastosIva,
                gastosRetIva: listGastosRetIva,
                gastosRetIsr: listGastosRetIsr,
                nominas: nominasList,
                nominasExentos: nominasList.filter(n => (n.conceptosExentosTotal || 0) > 0),
                nominasGravados: nominasList.filter(n => ((n.totalPercepciones || 0) - (n.conceptosExentosTotal || 0)) > 0),
                nominasIsr: nominasList.filter(n => (n.deducciones?.isr || 0) > 0)
            });

            setIvaCalculado({
                actosGravados,
                actosExentos,
                ivaTrasladado: ivaTrasladadoMes,
                ivaAcreditableGastos,
                ivaRetenido: ivaRetenidoGastos,
                saldoAFavorMes: saldoAFavorPeriodo,
                impuestoACargo: impuestoDefinitivo
            });

        } catch (error) {
            console.error("Error calculando fiscales:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatter = new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2
    });

    const formatTimestamp = (ts) => {
        if (!ts) return '-';
        if (ts.toDate) return ts.toDate().toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
        if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
        return new Date(ts).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const openDrilldown = (title, total, items, type) => {
        setDrilldownSearch('');
        setDrilldownModal({
            isOpen: true,
            title,
            total: total || 0,
            items: items || [],
            type
        });
    };

    const closeDrilldown = () => {
        setDrilldownModal(prev => ({ ...prev, isOpen: false }));
    };

    const filteredDrilldownItems = drilldownModal.items.filter(item => {
        if (!drilldownSearch) return true;
        const q = drilldownSearch.toLowerCase();
        if (drilldownModal.type === 'cargos') {
            return (
                (item.nombreResidente || item.residenteNombre || '').toLowerCase().includes(q) ||
                (item.unidadResidente || item.numeroCasa || '').toLowerCase().includes(q) ||
                (item.descripcion || item.concepto || '').toLowerCase().includes(q) ||
                (item.metodoPago || '').toLowerCase().includes(q) ||
                (item.folio || item.id || '').toLowerCase().includes(q)
            );
        } else if (drilldownModal.type === 'gastos') {
            return (
                (item.proveedor || '').toLowerCase().includes(q) ||
                (item.descripcion || '').toLowerCase().includes(q) ||
                (item.cuentaNombre || item.categoria || '').toLowerCase().includes(q) ||
                (item.folio || item.id || '').toLowerCase().includes(q)
            );
        } else if (drilldownModal.type === 'nominas') {
            return (
                (item.empleadoNombre || item.nombre || '').toLowerCase().includes(q) ||
                (item.puesto || '').toLowerCase().includes(q) ||
                (item.periodo || '').toLowerCase().includes(q)
            );
        }
        return JSON.stringify(item).toLowerCase().includes(q);
    });

    const toggleAccordion = (id) => {
        setExpandedSeccion(expandedSeccion === id ? '' : id);
    };

    const getAccordionClass = (id) => {
        return `fiscal-accordion ${expandedSeccion === id ? 'expanded' : ''}`;
    };

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute={activeRoute} />
            <main className="ks-main">
                <SafeAreaInsets top={false} bottom={true} />
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader showBack={false} />
                </div>
                <div className="ks-scroll-area">
                    <div className="fiscal-container" style={{ padding: '20px', paddingTop: '0px', maxWidth: '100%', minHeight: 'auto', background: 'transparent' }}>
                        {/* OMNI-BANNER PREMIUM */}
                        <div className="usuarios-fixed-wrapper" style={{ position: 'relative', marginTop: '0px' }}>
                            <div className="usuarios-banner-compact" style={{ background: isSaaS ? 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)' : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' }}>
                                <button onClick={() => navigate(isSaaS ? `${basePath}/dashboard` : '/gestion-finanzas')} className="usuarios-back-btn glass" title="Volver a Finanzas">
                                    <IoArrowBack size={24} />
                                </button>
                                <div className="usuarios-banner-icon">
                                    <IoFileTrayFullOutline size={120} opacity={0.1} />
                                </div>
                                <div className="usuarios-banner-content">
                                    <h2>{isSaaS ? 'Declaraciones SaaS' : 'Declaraciones y SAT'}</h2>
                                    <p>{isSaaS ? 'Proyección de Obligaciones Corporativas' : 'Simulador espejo tipo SAT/SIPARE para presentación mensual.'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="fiscal-scroll" style={{ marginTop: '20px', padding: '0 0 100px 0', overflowY: 'visible', height: 'auto', flex: 'none' }}>

                            {/* Selector Central de Modo (Causado vs Flujo) */}
                            <div className="fiscal-mode-toggle-container glass-card" style={{ marginBottom: '1.5rem', padding: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <h3 style={{ marginBottom: '1rem', color: '#1e293b' }}>Visualización del Ejercicio</h3>
                                <div className="fiscal-toggle-pill">
                                    <button
                                        className={`toggle-btn ${viewMode === 'causado' ? 'active Causado' : ''}`}
                                        onClick={() => setViewMode('causado')}
                                    >
                                        <IoAnalyticsOutline size={18} style={{ marginRight: '6px' }} />
                                        Impuestos Causados (Vigentes)
                                    </button>
                                    <button
                                        className={`toggle-btn ${viewMode === 'flujo' ? 'active Flujo' : ''}`}
                                        onClick={() => setViewMode('flujo')}
                                    >
                                        <IoBriefcaseOutline size={18} style={{ marginRight: '6px' }} />
                                        Proyección a Pagar (Flujo)
                                    </button>
                                </div>
                                {viewMode === 'causado' ? (
                                    <p style={{ marginTop: '0.8rem', fontSize: '0.85rem', color: '#64748b', textAlign: 'center' }}>
                                        Muestra los montos generados exactamente durante el mes seleccionado. Útil para métricas internas y P&L.
                                    </p>
                                ) : (
                                    <p style={{ marginTop: '0.8rem', fontSize: '0.85rem', color: '#047857', textAlign: 'center', background: '#ecfdf5', padding: '8px 16px', borderRadius: '8px' }}>
                                        Muestra el monto de la obligación que se transfiere al gobierno físicamente en este mes (Arrastra el mes previo en ISR/IMSS, y los bimestres previos en INFONAVIT).
                                    </p>
                                )}
                            </div>

                            {/* Seccion: Filtros & Config Global */}
                            <div className="fiscal-config-bar glass-card">
                                <div className="config-item">
                                    <label>Mes de Declaración (Target)</label>
                                    <input
                                        type="month"
                                        value={mesSeleccionado}
                                        onChange={(e) => setMesSeleccionado(e.target.value)}
                                    />
                                </div>
                                <div className="config-item">
                                    <label>Tasa Prima RT (IMSS) %</label>
                                    <input
                                        type="number"
                                        step="0.0001"
                                        value={primaRiesgo}
                                        onChange={(e) => setPrimaRiesgo(e.target.value)}
                                        min="0"
                                    />
                                </div>
                                <div className="config-item">
                                    <label>Tasa I.S.N (Estatal) %</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={tasaISN}
                                        onChange={(e) => setTasaISN(e.target.value)}
                                        min="0"
                                    />
                                </div>
                                <div className="config-item config-item-long">
                                    <label>Saldo a favor IVA ant.</label>
                                    <div className="money-input-mini">
                                        <span>$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={saldoFavorIVAAnterior}
                                            onChange={(e) => setSaldoFavorIVAAnterior(e.target.value)}
                                            min="0"
                                        />
                                    </div>
                                </div>
                            </div>

                            {loading ? (
                                <div className="fiscal-loading">Calculando base gravable y retenciones...</div>
                            ) : (
                                <div className="fiscal-accordions-wrapper">

                                    {/* ACCORDEON 1: ISR RETENCIONES POR SALARIOS (SAT) */}
                                    <div className={getAccordionClass('SAT_ISR')}>
                                        <div className="accordion-header" onClick={() => toggleAccordion('SAT_ISR')} style={{ background: 'linear-gradient(90deg, #be185d 0%, #9d174d 100%)' }}>
                                            <div className="header-titles">
                                                <IoDocumentTextOutline size={22} />
                                                <h3>SAT - Retenciones de ISR por Salarios</h3>
                                            </div>
                                            <span className="header-total-badge">{formatter.format(isrCalculado.cantidadAPagar)}</span>
                                        </div>
                                        <div className="accordion-body">
                                            <div className="sat-form-row">
                                                <span className="sat-label">Total de Ingresos Pagados en el Mes</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('Total de Ingresos Pagados en el Mes (Sueldos Brutos)', isrCalculado.totalIngresosPagados, detalleItems.nominas, 'nominas')} title="Clic para ver desglose">
                                                    {formatter.format(isrCalculado.totalIngresosPagados)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row">
                                                <span className="sat-label">(-) Ingresos Exentos Pagados al Trabajador</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('Ingresos Exentos Pagados al Trabajador', isrCalculado.ingresosExentos, detalleItems.nominasExentos, 'nominas')} title="Clic para ver desglose">
                                                    {formatter.format(isrCalculado.ingresosExentos)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row total-sub">
                                                <span className="sat-label">(=) Total de Ingresos Gravados</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('Total de Ingresos Gravados', isrCalculado.ingresosGravados, detalleItems.nominasGravados, 'nominas')} title="Clic para ver desglose">
                                                    {formatter.format(isrCalculado.ingresosGravados)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row highlight-row" style={{ marginTop: '1rem' }}>
                                                <span className="sat-label">ISR Retenido (Calculado Conforme a la Ley)</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('ISR Retenido por Salarios (Deducciones Nómina)', isrCalculado.impuestoRetenido, detalleItems.nominasIsr, 'nominas')} title="Clic para ver desglose">
                                                    {formatter.format(isrCalculado.impuestoRetenido)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row sat-total-cargo">
                                                <span className="sat-label">Cantidad A Cargo (A Pagar)</span>
                                                <button type="button" className="sat-value-drilldown-btn text-danger" onClick={() => openDrilldown('Cantidad a Cargo - ISR Salarios', isrCalculado.cantidadAPagar, detalleItems.nominasIsr, 'nominas')} title="Clic para ver desglose">
                                                    {formatter.format(isrCalculado.cantidadAPagar)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ACCORDEON 1-B: ISR RETENCIONES POR SERVICIOS PROFESIONALES / RESICO (SAT) */}
                                    <div className={getAccordionClass('SAT_ISR_PROV')}>
                                        <div className="accordion-header" onClick={() => toggleAccordion('SAT_ISR_PROV')} style={{ background: 'linear-gradient(90deg, #7c3aed 0%, #6d28d9 100%)' }}>
                                            <div className="header-titles">
                                                <IoDocumentTextOutline size={22} />
                                                <h3>SAT - Retenciones de ISR por Servicios Profesionales / RESICO</h3>
                                            </div>
                                            <span className="header-total-badge">{formatter.format(isrProveedoresCalculado.cantidadAPagar)}</span>
                                        </div>
                                        <div className="accordion-body">
                                            <div className="sat-form-row">
                                                <span className="sat-label">Base de los Servicios Prestados (Subtotal Pagado)</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('Base de Servicios Prestados (Honorarios / RESICO)', isrProveedoresCalculado.baseServicios, detalleItems.gastosRetIsr, 'gastos')} title="Clic para ver desglose">
                                                    {formatter.format(isrProveedoresCalculado.baseServicios)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row highlight-row" style={{ marginTop: '0.5rem' }}>
                                                <span className="sat-label">ISR Retenido (Calculado Conforme a la Ley)</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('ISR Retenido a Proveedores / RESICO', isrProveedoresCalculado.impuestoRetenido, detalleItems.gastosRetIsr, 'gastos')} title="Clic para ver desglose">
                                                    {formatter.format(isrProveedoresCalculado.impuestoRetenido)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row sat-total-cargo">
                                                <span className="sat-label">Cantidad A Cargo (A Pagar)</span>
                                                <button type="button" className="sat-value-drilldown-btn text-danger" onClick={() => openDrilldown('Cantidad a Pagar - Retenciones de ISR a Terceros', isrProveedoresCalculado.cantidadAPagar, detalleItems.gastosRetIsr, 'gastos')} title="Clic para ver desglose">
                                                    {formatter.format(isrProveedoresCalculado.cantidadAPagar)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ACCORDEON 2: IVA (SAT) */}
                                    <div className={getAccordionClass('SAT_IVA')}>
                                        <div className="accordion-header" onClick={() => toggleAccordion('SAT_IVA')} style={{ background: 'linear-gradient(90deg, #b45309 0%, #92400e 100%)' }}>
                                            <div className="header-titles">
                                                <IoAnalyticsOutline size={22} />
                                                <h3>SAT - Impuesto al Valor Agregado (IVA)</h3>
                                            </div>
                                            <span className="header-total-badge" style={{ backgroundColor: ivaCalculado.saldoAFavorMes > 0 ? '#10b981' : '#fef3c7', color: ivaCalculado.saldoAFavorMes > 0 ? 'white' : '#b45309' }}>
                                                {ivaCalculado.saldoAFavorMes > 0 ? `Favor ${formatter.format(ivaCalculado.saldoAFavorMes)}` : `Cargo ${formatter.format(ivaCalculado.impuestoACargo)}`}
                                            </span>
                                        </div>
                                        <div className="accordion-body">
                                            <div className="sat-form-row">
                                                <span className="sat-label">Valor de los actos o actividades gravados al 16% (Cobrados)</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('Actos o Actividades Gravados al 16% (Cobrados)', ivaCalculado.actosGravados, detalleItems.actosGravados, 'cargos')} title="Clic para ver desglose">
                                                    {formatter.format(ivaCalculado.actosGravados)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row">
                                                <span className="sat-label">Valor de los actos o actividades exentos (Cuotas Cobradas)</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('Actos o Actividades Exentos (Cuotas Cobradas)', ivaCalculado.actosExentos, detalleItems.actosExentos, 'cargos')} title="Clic para ver desglose">
                                                    {formatter.format(ivaCalculado.actosExentos)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row total-sub" style={{ marginTop: '1rem' }}>
                                                <span className="sat-label">(=) IVA Trasladado Cobrado (A)</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('IVA Trasladado Cobrado (A)', ivaCalculado.ivaTrasladado, detalleItems.actosGravados, 'cargos')} title="Clic para ver desglose">
                                                    {formatter.format(ivaCalculado.ivaTrasladado)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row text-secondary">
                                                <span className="sat-label">(-) IVA Acreditable del Periodo (Gastos Pagados) (B)</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('IVA Acreditable del Periodo (Gastos Pagados)', ivaCalculado.ivaAcreditableGastos, detalleItems.gastosIva, 'gastos')} title="Clic para ver desglose">
                                                    {formatter.format(ivaCalculado.ivaAcreditableGastos)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row text-secondary">
                                                <span className="sat-label">(-) IVA Retenido (C)</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('IVA Retenido a Proveedores (C)', ivaCalculado.ivaRetenido, detalleItems.gastosRetIva, 'gastos')} title="Clic para ver desglose">
                                                    {formatter.format(ivaCalculado.ivaRetenido)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row text-secondary">
                                                <span className="sat-label">(-) Saldo a favor de periodos anteriores (D)</span>
                                                <span className="sat-value">{formatter.format(Number(saldoFavorIVAAnterior))}</span>
                                            </div>
                                            <div className={`sat-form-row sat-total-cargo ${ivaCalculado.saldoAFavorMes > 0 ? 'text-success' : 'text-danger'}`} style={{ marginTop: '1rem' }}>
                                                <span className="sat-label">{ivaCalculado.saldoAFavorMes > 0 ? "Saldo a Favor del Periodo" : "Impuesto a Cargo"} (A-B-C-D)</span>
                                                <button type="button" className={`sat-value-drilldown-btn ${ivaCalculado.saldoAFavorMes > 0 ? 'text-success' : 'text-danger'}`} onClick={() => openDrilldown('Resultado Neto IVA del Periodo', ivaCalculado.saldoAFavorMes > 0 ? ivaCalculado.saldoAFavorMes : ivaCalculado.impuestoACargo, [...detalleItems.actosGravados, ...detalleItems.gastosIva], 'mixto_iva')} title="Clic para ver desglose">
                                                    {formatter.format(ivaCalculado.saldoAFavorMes > 0 ? ivaCalculado.saldoAFavorMes : ivaCalculado.impuestoACargo)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ACCORDEON 3: SIPARE / IMSS */}
                                    <div className={getAccordionClass('IMSS')}>
                                        <div className="accordion-header" onClick={() => toggleAccordion('IMSS')} style={{ background: 'linear-gradient(90deg, #059669 0%, #047857 100%)' }}>
                                            <div className="header-titles">
                                                <IoBriefcaseOutline size={22} />
                                                <h3>IMSS/Infonavit - Cargas Sociales (SIPARE)</h3>
                                            </div>
                                            <span className="header-total-badge">{formatter.format(imssCalculado.granTotalSIPARE)}</span>
                                        </div>
                                        <div className="accordion-body imss-body">
                                            <div className="imss-columns">
                                                <div className="imss-col">
                                                    <h4 className="imss-subhead">Cuota Retenida (Obrero)</h4>
                                                    <div className="sat-form-row">
                                                        <span className="sat-label">Retenciones Globales Nómina</span>
                                                        <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('Retenciones Cuota Obrera IMSS Nómina', imssCalculado.retencionObreraTotal, detalleItems.nominas, 'nominas')} title="Clic para ver desglose">
                                                            {formatter.format(imssCalculado.retencionObreraTotal)}
                                                            <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                        </button>
                                                    </div>
                                                    <div className="sat-form-row">
                                                        <span className="sat-label">Amortización Crédito INFONAVIT</span>
                                                        <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('Amortizaciones de Crédito INFONAVIT', imssCalculado.retencionInfonavitTotal, detalleItems.nominas, 'nominas')} title="Clic para ver desglose">
                                                            {formatter.format(imssCalculado.retencionInfonavitTotal)}
                                                            <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                        </button>
                                                    </div>
                                                    <div style={{ marginTop: '2rem', fontSize: '0.8rem', color: '#64748b', paddingRight: '1rem' }}>
                                                        <strong>Nota Periodicidad:</strong><br />
                                                        Las cuotas obrero-patronales del IMSS se enteran <strong>Mensualmente</strong> (ej. Enero se paga en Febrero).<br /><br />
                                                        Las aportaciones del 5% al INFONAVIT, Amortizaciones de Crédito y las cuotas de Retiro, Cesantía y Vejez (RCV) se enteran <strong>Bimestralmente</strong> (ej. Ene-Feb se paga en Marzo).<br /><br />
                                                        {viewMode === 'causado' ? (
                                                            <i>Este simulador muestra la causación íntegra generada en el mes seleccionado.</i>
                                                        ) : (
                                                            <i style={{ color: '#047857' }}>
                                                                Estás en modo Flujo a Pagar. Exclusivamente para INFONAVIT/RCV, el sistema suma los dos meses del bimestre anterior dependiendo del mes de pago. Si seleccionas un mes par (ej. Abril) las obligaciones bimestrales se mostrarán en $0 ya que no hay vencimiento.
                                                            </i>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="imss-col">
                                                    <h4 className="imss-subhead">Desglose Patronal (Global)</h4>
                                                    <div className="sat-form-row">
                                                        <span className="sat-label">Cuota Fija (Especie)</span>
                                                        <span className="sat-value">{formatter.format(imssCalculado.cuotaFijaEspecie)}</span>
                                                    </div>
                                                    <div className="sat-form-row">
                                                        <span className="sat-label">Exc. Patronal (3 UMAS)</span>
                                                        <span className="sat-value">{formatter.format(imssCalculado.cuotaExcedente)}</span>
                                                    </div>
                                                    <div className="sat-form-row">
                                                        <span className="sat-label">Gastos Méd. Pensionados</span>
                                                        <span className="sat-value">{formatter.format(imssCalculado.cuotaGastosMedicos)}</span>
                                                    </div>
                                                    <div className="sat-form-row">
                                                        <span className="sat-label">Prestaciones Dinero</span>
                                                        <span className="sat-value">{formatter.format(imssCalculado.cuotaEnDinero)}</span>
                                                    </div>
                                                    <div className="sat-form-row">
                                                        <span className="sat-label">Riesgos de Trabajo</span>
                                                        <span className="sat-value">{formatter.format(imssCalculado.cuotaRiesgoTrabajo)}</span>
                                                    </div>
                                                    <div className="sat-form-row">
                                                        <span className="sat-label">Invalidez y Vida</span>
                                                        <span className="sat-value">{formatter.format(imssCalculado.cuotaInvalidezVida)}</span>
                                                    </div>
                                                    <div className="sat-form-row">
                                                        <span className="sat-label">Guarderías y Prestaciones Soc.</span>
                                                        <span className="sat-value">{formatter.format(imssCalculado.cuotaGuarderias)}</span>
                                                    </div>
                                                    <div className="sat-form-row" style={{ marginTop: '0.8rem', paddingTop: '0.8rem', borderTop: '1px dashed #ccc' }}>
                                                        <span className="sat-label">Seguro de Retiro (2%)</span>
                                                        <span className="sat-value">{formatter.format(imssCalculado.seguroRetiro)}</span>
                                                    </div>
                                                    <div className="sat-form-row">
                                                        <span className="sat-label">Cesantía y Vejez Patronal</span>
                                                        <span className="sat-value">{formatter.format(imssCalculado.cesantiaVejez)}</span>
                                                    </div>
                                                    <div className="sat-form-row" style={{ marginTop: '0.8rem', paddingTop: '0.8rem', borderTop: '1px dashed #ccc' }}>
                                                        <span className="sat-label">Aportación INFONAVIT (5%)</span>
                                                        <span className="sat-value">{formatter.format(imssCalculado.aportacionInfonavit)}</span>
                                                    </div>

                                                    <div className="sat-form-row total-sub">
                                                        <span className="sat-label">(=) Total Carga Patronal</span>
                                                        <span className="sat-value">{formatter.format(imssCalculado.totalCargaPatronal)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="sat-form-row sat-total-cargo" style={{ marginTop: '1.5rem', background: '#ecfdf5', borderColor: '#a7f3d0' }}>
                                                <span className="sat-label">Total Liquidación SIPARE (Obrero + Patrón)</span>
                                                <button type="button" className="sat-value-drilldown-btn text-success" onClick={() => openDrilldown('Liquidación SIPARE (IMSS / Infonavit)', imssCalculado.granTotalSIPARE, detalleItems.nominas, 'nominas')} title="Clic para ver desglose">
                                                    {formatter.format(imssCalculado.granTotalSIPARE)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ACCORDEON 4: ISN */}
                                    <div className={getAccordionClass('ISN')}>
                                        <div className="accordion-header" onClick={() => toggleAccordion('ISN')} style={{ background: 'linear-gradient(90deg, #6366f1 0%, #4338ca 100%)' }}>
                                            <div className="header-titles">
                                                <IoDocumentTextOutline size={22} />
                                                <h3>ISN - Impuesto Sobre Nómina (Estatal)</h3>
                                            </div>
                                            <span className="header-total-badge">{formatter.format(isnCalculado.impuestoAPagar)}</span>
                                        </div>
                                        <div className="accordion-body">
                                            <div className="sat-form-row">
                                                <span className="sat-label">Base Gravable (Remuneraciones Pagadas)</span>
                                                <button type="button" className="sat-value-drilldown-btn" onClick={() => openDrilldown('Base Gravable ISN (Nómina Bruta)', isnCalculado.baseGravable, detalleItems.nominas, 'nominas')} title="Clic para ver desglose">
                                                    {formatter.format(isnCalculado.baseGravable)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                            <div className="sat-form-row text-secondary">
                                                <span className="sat-label">Tasa Aplicable (%)</span>
                                                <span className="sat-value">{tasaISN}%</span>
                                            </div>
                                            <div className="sat-form-row sat-total-cargo">
                                                <span className="sat-label">Impuesto a Cargo</span>
                                                <button type="button" className="sat-value-drilldown-btn text-danger" onClick={() => openDrilldown('Impuesto Sobre Nómina a Pagar', isnCalculado.impuestoAPagar, detalleItems.nominas, 'nominas')} title="Clic para ver desglose">
                                                    {formatter.format(isnCalculado.impuestoAPagar)}
                                                    <IoChevronForwardOutline size={14} className="drilldown-arrow" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <KondoBottomNav activeRoute={activeRoute} />

                {/* MODAL DE DESGLOSE / DRILLDOWN DINÁMICO */}
                {drilldownModal.isOpen && (
                    <div className="sat-drilldown-overlay" onClick={closeDrilldown}>
                        <div className="sat-drilldown-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="sat-drilldown-header">
                                <div>
                                    <h3>{drilldownModal.title}</h3>
                                    <p>Periodo: {mesSeleccionado} • Desglose individual de registros</p>
                                </div>
                                <button className="sat-drilldown-close-btn" onClick={closeDrilldown} title="Cerrar">
                                    <IoCloseOutline size={22} />
                                </button>
                            </div>

                            <div className="sat-drilldown-toolbar">
                                <div className="sat-drilldown-stat">
                                    <span className="sat-drilldown-stat-label">Total del Concepto:</span>
                                    <span className="sat-drilldown-stat-val">{formatter.format(drilldownModal.total)}</span>
                                    <span className="sat-badge-tag" style={{ marginLeft: '8px' }}>
                                        {drilldownModal.items.length} registro(s)
                                    </span>
                                </div>
                                <div className="sat-drilldown-search-wrap">
                                    <IoSearchOutline size={16} className="sat-drilldown-search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Buscar residente, unidad, proveedor..."
                                        value={drilldownSearch}
                                        onChange={(e) => setDrilldownSearch(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div className="sat-drilldown-content">
                                {filteredDrilldownItems.length === 0 ? (
                                    <div className="sat-drilldown-empty">
                                        <IoInformationCircleOutline size={48} color="#94a3b8" />
                                        <p>No se encontraron registros {drilldownSearch ? 'que coincidan con la búsqueda' : 'en este periodo para este concepto'}.</p>
                                    </div>
                                ) : (
                                    <table className="sat-drilldown-table">
                                        <thead>
                                            {drilldownModal.type === 'cargos' && (
                                                <tr>
                                                    <th>Fecha Pago</th>
                                                    <th>Unidad / Residente</th>
                                                    <th>Concepto</th>
                                                    <th>Método</th>
                                                    <th style={{ textAlign: 'right' }}>Monto Base</th>
                                                    <th style={{ textAlign: 'right' }}>IVA</th>
                                                    <th style={{ textAlign: 'right' }}>Total</th>
                                                </tr>
                                            )}
                                            {drilldownModal.type === 'gastos' && (
                                                <tr>
                                                    <th>Fecha</th>
                                                    <th>Proveedor</th>
                                                    <th>Descripción</th>
                                                    <th style={{ textAlign: 'right' }}>Subtotal</th>
                                                    <th style={{ textAlign: 'right' }}>IVA Acred.</th>
                                                    <th style={{ textAlign: 'right' }}>Ret. IVA</th>
                                                    <th style={{ textAlign: 'right' }}>Ret. ISR</th>
                                                    <th style={{ textAlign: 'right' }}>Total Pagado</th>
                                                </tr>
                                            )}
                                            {drilldownModal.type === 'nominas' && (
                                                <tr>
                                                    <th>Fecha / Periodo</th>
                                                    <th>Empleado</th>
                                                    <th>Puesto</th>
                                                    <th style={{ textAlign: 'right' }}>Bruto</th>
                                                    <th style={{ textAlign: 'right' }}>Exento</th>
                                                    <th style={{ textAlign: 'right' }}>Gravado</th>
                                                    <th style={{ textAlign: 'right' }}>ISR Ret.</th>
                                                    <th style={{ textAlign: 'right' }}>IMSS Obr.</th>
                                                    <th style={{ textAlign: 'right' }}>Neto</th>
                                                </tr>
                                            )}
                                            {drilldownModal.type === 'mixto_iva' && (
                                                <tr>
                                                    <th>Tipo</th>
                                                    <th>Fecha</th>
                                                    <th>Referencia / Beneficiario</th>
                                                    <th>Concepto</th>
                                                    <th style={{ textAlign: 'right' }}>Importe</th>
                                                    <th style={{ textAlign: 'right' }}>IVA Efectivo</th>
                                                </tr>
                                            )}
                                        </thead>
                                        <tbody>
                                            {filteredDrilldownItems.map((item, idx) => {
                                                if (drilldownModal.type === 'cargos') {
                                                    const monto = Number(item.monto || item.montoPagado || 0);
                                                    const iva = Number(item.iva || 0);
                                                    const base = iva > 0 ? (monto - iva) : monto;
                                                    return (
                                                        <tr key={item.id || idx}>
                                                            <td>{formatTimestamp(item.fechaPago || item.fecha)}</td>
                                                            <td>
                                                                <strong>{item.unidadResidente || item.numeroCasa || 'Unidad'}</strong>
                                                                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{item.nombreResidente || item.residenteNombre || ''}</div>
                                                            </td>
                                                            <td>{item.descripcion || item.concepto || 'Cuota de Mantenimiento'}</td>
                                                            <td><span className="sat-badge-tag">{item.metodoPago || 'General'}</span></td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatter.format(base)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatter.format(iva)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>{formatter.format(monto)}</td>
                                                        </tr>
                                                    );
                                                } else if (drilldownModal.type === 'gastos') {
                                                    const subtotal = Number(item.subtotal || item.monto || 0);
                                                    const iva = Number(item.iva || 0);
                                                    const retIva = Number(item.retencionIva || 0);
                                                    const retIsr = Number(item.retencionIsr || item.retencionISR || item.isrRetenido || 0);
                                                    const total = Number(item.monto || (subtotal + iva - retIva - retIsr));
                                                    return (
                                                        <tr key={item.id || idx}>
                                                            <td>{formatTimestamp(item.fecha)}</td>
                                                            <td>
                                                                <strong>{item.proveedor || 'Proveedor'}</strong>
                                                                {item.cuentaNombre && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{item.cuentaNombre}</div>}
                                                            </td>
                                                            <td>{item.descripcion || '-'}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatter.format(subtotal)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatter.format(iva)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace', color: retIva > 0 ? '#b91c1c' : 'inherit' }}>{formatter.format(retIva)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace', color: retIsr > 0 ? '#b91c1c' : 'inherit' }}>{formatter.format(retIsr)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>{formatter.format(total)}</td>
                                                        </tr>
                                                    );
                                                } else if (drilldownModal.type === 'nominas') {
                                                    const perc = Number(item.totalPercepciones || 0);
                                                    const exento = Number(item.conceptosExentosTotal || 0);
                                                    const gravado = perc - exento;
                                                    const isr = Number(item.deducciones?.isr || 0);
                                                    const imss = Number(item.deducciones?.imss || 0);
                                                    const neto = Number(item.totalNeto || (perc - (item.totalDeduccionesVal || 0)));
                                                    return (
                                                        <tr key={item.id || idx}>
                                                            <td>{formatTimestamp(item.fechaGeneracion)}</td>
                                                            <td><strong>{item.empleadoNombre || item.nombre || 'Trabajador'}</strong></td>
                                                            <td><span className="sat-badge-tag">{item.puesto || 'General'}</span></td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatter.format(perc)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatter.format(exento)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatter.format(gravado)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace', color: isr > 0 ? '#b91c1c' : 'inherit' }}>{formatter.format(isr)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatter.format(imss)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>{formatter.format(neto)}</td>
                                                        </tr>
                                                    );
                                                } else {
                                                    // mixto_iva
                                                    const isCargo = Boolean(item.fechaPago || item.unidadResidente || item.residenteId);
                                                    const monto = Number(item.monto || item.montoPagado || 0);
                                                    const iva = Number(item.iva || 0);
                                                    return (
                                                        <tr key={item.id || idx}>
                                                            <td>
                                                                <span className="sat-badge-tag" style={{ background: isCargo ? '#dcfce7' : '#fee2e2', color: isCargo ? '#166534' : '#991b1b' }}>
                                                                    {isCargo ? 'Ingreso (Trasladado)' : 'Gasto (Acreditable)'}
                                                                </span>
                                                            </td>
                                                            <td>{formatTimestamp(isCargo ? (item.fechaPago || item.fecha) : item.fecha)}</td>
                                                            <td><strong>{isCargo ? (item.unidadResidente || item.nombreResidente) : (item.proveedor || 'Proveedor')}</strong></td>
                                                            <td>{item.descripcion || item.concepto || '-'}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatter.format(monto)}</td>
                                                            <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold', color: isCargo ? '#16a34a' : '#2563eb' }}>
                                                                {formatter.format(iva)}
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="sat-drilldown-footer">
                                <button className="sat-drilldown-btn-close" onClick={closeDrilldown}>
                                    Cerrar Desglose
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default ObligacionesFiscalesScreen;
